<script setup lang="ts">
import { h, ref } from 'vue'
import type { ImporterDetail } from '@/infrastructure/types';
import { useUpdate, usePatch, useFetch, useCreate } from '@/plugins/api';
import { Archive20Regular, Play20Regular, Save20Regular, ArrowReset20Regular, CalendarEdit20Regular } from '@vicons/fluent';
import { formatDateTime } from '@/composables/useFormatDate';
import { formatTimeAgo } from '@vueuse/core';
import { NAlert, NButton, NFlex, NForm, NFormItem, useModal } from 'naive-ui';
import DatePickerComponent from './DatePickerComponent.vue';

let interval: NodeJS.Timeout;
const inProgressStates = ['Pending', 'Reserved', 'InProgress']
const modal = useModal()

const props = defineProps<{
  importerId: number
}>()
const importer = ref<ImporterDetail>(await useFetch('importers/' + props.importerId))
const saving = ref(false)
const running = computed(() => inProgressStates.includes(importer.value.lastImport_ImportState))

watch(running, (value) => {
  if (value) {
    pollForChanges()
  } else {
    clearInterval(interval)
  }
}, {
  immediate: true
})

async function runImporter() {
  try {
    await update(false)
    await useCreate(`importers/${props.importerId}/run`, null, {
      successMessage: 'Importer job created'
    })
  } catch (error) {
    console.error(error)
  } finally {
    getData()
  }
}

async function activate() {
  await usePatch('importers/' + importer.value.id + '/set-active')
  getData()
}

async function deactivate() {
  await usePatch('importers/' + importer.value.id + '/set-active?active=false')
  getData()
}

function getStateColor(state: string): string {
  switch (state) {
    case 'Completed':
      return 'success'
    case 'Failed':
      return 'error'
    default:
      return ''
  }
}

function pollForChanges() {
  interval = setInterval(getData, 1000);
}

function showSetMaxTransactionDateModal() {
  const loading = ref(false)
  const date = ref(importer.value.maxTransactionDate)

  async function setMaxTransactionDate() {
    loading.value = true

    try {
      await usePatch(`importers/${props.importerId}/set-maxtransactiondate${date.value ? '?dateTime=' + date.value : ''}`)
      modal.destroyAll()
      getData()
    } finally {
      loading.value = false
    }
  }

  modal.create({
    title: 'Set max transaction date',
    preset: 'card',
    style: {
      width: '500px'
    },
    content: () => [
      h(NAlert, { type: 'warning', class: 'mb-4' }, () => 'Make sure you know what you\'re doing...'),
      h(NFormItem, { label: 'Max transaction date' }, () => h(DatePickerComponent, {
        formattedValue: date.value,
        onUpdateFormattedValue: (val: string) => date.value = val
      }))
    ],
    action: () => h(NFlex, null, () => [
      h(NButton, {
        type: 'success',
        loading: loading.value,
        onClick: () => setMaxTransactionDate()
      }, () => 'Save'),
    ])
  })
}

async function update(getNewData: boolean = true) {
  saving.value = true

  try {
    await useUpdate(`importers/${props.importerId}`, importer.value)

    if (getNewData) {
      getData()
    }
  } finally {
    saving.value = false
  }
}

async function getData() {
  importer.value = await useFetch('importers/' + props.importerId)
}

// onMounted(() => {
//   if (inProgressStates.includes(importer.value.lastImport_ImportState)) {
//     pollForChanges()
//   }
// })

onBeforeUnmount(() => {
  clearInterval(interval)
})
</script>

<template>
  <n-card size="small">
    <template #header>
      <div class="flex gap-2 items-center">
        <span class="text-lg">{{ importer.typeName }}</span>
        <span class="text-xs" v-if="importer.averageDurationInSeconds">Average duration: <b>{{ importer.averageDurationInSeconds }} seconds</b></span>
        <span class="text-xs">
          Latest transaction: {{ importer.maxTransactionDate ? formatDateTime(importer.maxTransactionDate) : 'Never' }}
        </span>
      </div>
    </template>
    <template #header-extra>
      <n-flex align="center">
        <n-button v-if="importer.configuration.active" @click="deactivate" :render-icon="() => h(Archive20Regular)" secondary>
          Archive
        </n-button>
        <n-button v-else @click="activate" :render-icon="() => h(ArrowReset20Regular)">
          Restore
        </n-button>
        <cdi-importer-history :importer-id="importer.id" />
        <n-button :loading="running" :disabled="running" :render-icon="() => h(Play20Regular)" @click="runImporter">
          {{ running ? 'Running' : 'Run now' }}
        </n-button>
        <n-button type="success" :render-icon="() => h(Save20Regular)" @click="update()">
          Save
        </n-button>
      </n-flex>
    </template>
    <template #default>
      <div class="flex flex-col gap-10 w-full">
        <n-card size="small" embedded v-if="importer.lastImport_StartTime">
          <template #header>
            <n-flex align="baseline">
              <div class="font-bold">Last run</div>
              <div class="text-sm">
                @
                {{ formatDateTime(importer.lastImport_StartTime) }} ({{ formatTimeAgo(new Date(importer.lastImport_StartTime)) }})
              </div>
            </n-flex>
          </template>
          <template #header-extra>
            <n-flex align="center">
              <span>
                Duration: {{ importer.lastImport_DurationInSeconds ? `${importer.lastImport_DurationInSeconds} seconds` : 'Unknown' }}
              </span>
              <span>
                Progress: {{ importer.lastImport_ProgressInPercent }}%
              </span>
              <n-tag :type="getStateColor(importer.lastImport_ImportState)" :bordered="false">
                {{ importer.lastImport_ImportState }}
              </n-tag>
            </n-flex>
          </template>
          <template #default>
            <div>
              <div class="mb-2 font-bold">Message</div>
              <n-log
                :log="importer.lastImport_Message"
                :font-size="12"
                :rows="10"
              ></n-log>
            </div>
          </template>
        </n-card>

        <n-card v-else embedded size="small">
          No runs yet
        </n-card>

        <n-thing title="Configuration">
          <template #header-extra>
            <n-modal>

            </n-modal>
            <n-button size="small" :render-icon="() => h(CalendarEdit20Regular)" @click="showSetMaxTransactionDateModal">
              Set max transaction date
            </n-button>
          </template>

          <n-form label-placement="top" label-width="auto" label-align="left">
            <n-form-item label="Start">
              <cdi-date-picker v-model:formatted-value="importer.configuration.start" type="date"></cdi-date-picker>
            </n-form-item>
            <n-form-item label="Execution timeout">
              <n-input-number v-model:value="importer.configuration.executeTimeoutInSeconds">
                <template #suffix>
                  seconds
                </template>
              </n-input-number>
            </n-form-item>
            <n-form-item label="Expires on">
              <cdi-date-picker v-model:formatted-value="importer.configuration.expiresOn"></cdi-date-picker>
            </n-form-item>
            <n-form-item label="Expire description">
              <n-input v-model:value="importer.configuration.expireDescription" type="textarea" clearable></n-input>
            </n-form-item>

            <n-table class="text-xs">
              <thead>
                <tr>
                  <th>Input name</th>
                  <th style="width: 20%;">Description</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th style="width: 40%">Value</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="input in importer.configuration.values" :key="input.name">
                  <td>{{ input.name }}</td>
                  <td>{{ input.description }}</td>
                  <td>{{ input.type }}</td>
                  <td>{{ input.required }}</td>
                  <td>{{ input.defaultValue }}</td>
                  <td>
                    <n-input
                      v-if="input.type === 'Default' || input.type === 'String'"
                      v-model:value="input.value"
                      clearable
                    />
                    <n-input-number
                      v-else-if="input.type === 'Int'"
                      v-model:value="input.value"
                      clearable
                    />
                    <n-checkbox
                      v-else-if="input.type === 'Bool'"
                      v-model:checked="input.value"
                    />
                    <n-input-group v-else-if="input.type === 'Folder'">
                      <!-- <n-input-group-label>ftp://we/need/the/setting</n-input-group-label> -->
                      <n-input v-model:value="input.value" clearable />
                      <!-- <n-button>Browse</n-button> -->
                    </n-input-group>
                    <cdi-date-picker v-if="input.type === 'DateTime'" v-model:formatted-value="input.value"></cdi-date-picker>
                  </td>
                </tr>
              </tbody>
            </n-table>
          </n-form>
        </n-thing>
      </div>
    </template>
  </n-card>
</template>